import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useSourceContext } from "libs/context/SourceContext";
import SourceAddEditModal from "pages/AllCommonModal/SourceAddEditModal/source-add-edit-modal";
import { submitFilterData } from "model/SourceModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


const Source = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchSourceContextData, sourceContextData } = useSourceContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Source");
  const initialFilterFetchedRef = useRef(false);
  const [sourceAddEditModalOpen, setSourceAddEditModalOpen] = useState(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceData, setSourceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const initialState = {
    title: "",
    candidateDeactive: false,
    forTools: false,
    status: true,
  };

  const [formData, setFormData] = useState(initialState);

  const filteredData = useMemo(() => sourceData, [sourceData]);

  const sourceActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.SourceAddAccess),
      canEdit: Boolean(permissionData?.SourceUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnySourceRowAction = useMemo(
    () => sourceActionPermissions.canEdit,
    [sourceActionPermissions]
  );

  const handleEdit = (row) => {
    setItemId(row?.id || row?.jobCategoryId);
    setSourceAddEditModalOpen(true);
  };

  useEffect(() => {
    fetchSourceContextData();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getSourceList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = sourceContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: col?.field,
    }));
    setTableMetadata(valueReModified);
  }, [sourceContextData]);

  const getSourceList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitFilterData(payload);
    const responseData = response?.data || {};
    const listData =
      responseData?.records ?? (Array.isArray(responseData) ? responseData : []);
    const valueReModified = (Array.isArray(listData) ? listData : []).map((item) => ({
      ...item,
      ID: item?.id || item?.jobCategoryId,
    }));

    if (response) {
      setSourceData(valueReModified);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || valueReModified.length,
      });
      setRequestPayload(payload);
    } else {
      setSourceData([]);
    }
    setLoading(false);
  };

  const handleFilterSubmit = async (data) => {
    await getSourceList({
      ...data,
      search: requestPayload?.search || "",
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    await getSourceList({
      ...requestPayload,
      pageNumber: 1,
      search: trimmedValue,
    });
  };

  const handlePageChange = async (page) => {
    await getSourceList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getSourceList({ pageNumber: 1, pageSize: rows });
  };


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          {/* Table */}
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Source</h5>



                {sourceActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setSourceAddEditModalOpen(true);
                        setFormData(initialState);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>


              </Row>

              {/* <hr /> */}
              {/* <div className="mb-4 h4 card-title">Job Category</div> */}
              {!loading && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnySourceRowAction}
                  onEdit={
                    sourceActionPermissions.canEdit ? handleEdit : undefined
                  }
                  isEditAccess={sourceActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"Source"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>

          {/* Add/Edit Modal */}
          <SourceAddEditModal
            setSourceAddEditModalOpen={setSourceAddEditModalOpen}
            sourceAddEditModalOpen={sourceAddEditModalOpen}
            getSourceList={getSourceList}
            itemId={itemId}
            setSourceData={setSourceData}
            setFormData={setFormData}
            formData={formData}
            totalData={sourceData}
          />
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Source"
            handleSubmit={handleFilterSubmit}
            filterData={sourceContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

Source.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(Source);
